// @ts-nocheck
/*
 * Port of disposition-follow-ups/list.js (830 lines). BYTE-IDENTICAL.
 * A follow-ups list with draft-then-apply date/status filters, quick stats
 * (total / pending / completed / next follow-up), sortable columns, and
 * a per-row details modal that unwraps `disposition_details.fields` into
 * a readable table. Can render standalone (routed page) OR embedded in a
 * side-panel (`embedded=true`) â€” v1 usage from PendingDispositionsPanel;
 * `active` gates fetches so the panel doesn't refetch when hidden.
 * Follow-up date range clamped to 3 months. Status "overdue" is a client
 * filter applied over the fetched pending rows.
 * v1 imports Card from a thin `<div class="card">` wrapper â€” v2 uses the
 * bare tag with the same class so DFU CSS drives everything.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import {
  Calendar,
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  FileText,
  Phone,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, patchApiCall } from '@/service/httpCall';
import {
  API_DISPOSITION_FOLLOW_UPS_LIST,
  API_DISPOSITION_FOLLOW_UP_STATUS,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { LIST_PAGE_SIZE_OPTIONS } from '@/utilities/listPagination';
import { canUseDispositionFollowUpsFeature } from '@/utilities/dispositionFormsAcl';
import './disposition-follow-ups.css';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const TZ = 'Asia/Kolkata';

const MAX_FOLLOW_UP_RANGE_MONTHS = 3;

const formatDateOnlyLocal = (date: Date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};

const getDefaultFollowUpDateRange = () => {
  const now = new Date();
  const to = formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30));
  const from = formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), 1));
  return { from, to };
};

const isFollowUpRangeWithinLimit = (fromDate: string, toDate: string) => {
  if (!fromDate || !toDate) return true;
  const from = new Date(`${fromDate}T00:00:00`);
  const maxTo = new Date(from);
  maxTo.setMonth(maxTo.getMonth() + MAX_FOLLOW_UP_RANGE_MONTHS);
  const to = new Date(`${toDate}T00:00:00`);
  return to.getTime() <= maxTo.getTime();
};

const DEFAULT_FOLLOW_UP_DATE_RANGE = getDefaultFollowUpDateRange();

const getInitials = (name: string) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'AG';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const normalizeTime = (value: any) => {
  if (!value) return '00:00:00';
  const str = String(value).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(str)) return str;
  if (/^\d{2}:\d{2}$/.test(str)) return `${str}:00`;
  return str;
};

const getFollowUpTimestamp = (row: any) => {
  if (!row?.follow_up_date) return 0;
  const dateStr = String(row.follow_up_date).slice(0, 10);
  const timeStr = normalizeTime(row.follow_up_time);
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const fmtFollowUpDateTime = (row: any) => {
  const ts = getFollowUpTimestamp(row);
  if (!ts) return '-';
  return new Date(ts).toLocaleString('en-IN', {
    timeZone: TZ,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const fmtCreated = (value: any) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: TZ,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const formatDispositionFieldValue = (val: any) => {
  if (val === null || val === undefined || val === '') return '-';
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

const sourceLabel = (src: any) => {
  if (!src) return '-';
  return String(src).replace(/_/g, ' ');
};

const StatusBadge = ({ status }: any) => {
  const key = String(status || 'pending').toLowerCase();
  return (
    <span className={`dfu-status ${key}`}>
      <span className="dfu-status-dot" />
      {key}
    </span>
  );
};

const SortHeader = ({ label, field, sortField, sortDir, onSort }: any) => (
  <th
    className="sortable"
    onClick={() => onSort(field)}
    aria-sort={
      sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
    }
  >
    {label}
    {sortField === field ? (
      sortDir === 'asc' ? (
        <ChevronUp className="ms-1" aria-hidden="true" style={{ width: 14, height: 14 }} />
      ) : (
        <ChevronDown className="ms-1" aria-hidden="true" style={{ width: 14, height: 14 }} />
      )
    ) : (
      <ChevronsUpDown className="ms-1 text-muted"
        aria-hidden="true"
        style={{ width: 14, height: 14, opacity: 0.4 }}
      />
    )}
  </th>
);

const DispositionFollowUpsList = ({ embedded = false, onClose, active = true }: any) => {
  const [allRows, setAllRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<any>(null);

  const filterKey = `disposition-follow-ups/DispositionFollowUps${embedded ? '-embedded' : ''}`;
  const [draftStatus, setDraftStatus] = usePersistedState(`${filterKey}:draftStatus`, '');
  const [draftFromDate, setDraftFromDate] = usePersistedState(`${filterKey}:draftFromDate`, DEFAULT_FOLLOW_UP_DATE_RANGE.from);
  const [draftToDate, setDraftToDate] = usePersistedState(`${filterKey}:draftToDate`, DEFAULT_FOLLOW_UP_DATE_RANGE.to);
  const [draftSearch, setDraftSearch] = usePersistedState(`${filterKey}:draftSearch`, '');

  const [appliedStatus, setAppliedStatus] = usePersistedState(`${filterKey}:appliedStatus`, '');
  const [appliedFromDate, setAppliedFromDate] = usePersistedState(`${filterKey}:appliedFromDate`, DEFAULT_FOLLOW_UP_DATE_RANGE.from);
  const [appliedToDate, setAppliedToDate] = usePersistedState(`${filterKey}:appliedToDate`, DEFAULT_FOLLOW_UP_DATE_RANGE.to);
  const [appliedSearch, setAppliedSearch] = usePersistedState(`${filterKey}:appliedSearch`, '');

  const [sortField, setSortField] = usePersistedState(`${filterKey}:sortField`, 'follow_up');
  const [sortDir, setSortDir] = usePersistedState(`${filterKey}:sortDir`, 'asc');
  const [page, setPage] = usePersistedState(`${filterKey}:page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${filterKey}:pageSize`, LIST_PAGE_SIZE_OPTIONS[0]);
  // The first completed load keeps the saved page; later loads go back to page 1.
  const keepSavedPageRef = useRef(true);
  const [detailsRow, setDetailsRow] = useState<any>(null);

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;
  const isCallCentreAdmin = user?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const isAgent = user?.role === USER_ROLES.CALL_CENTRE_AGENT;
  const dispositionAcl = useModuleAcl(MODULE_ACL.DISPOSITION_FORMS);
  const showNextFollowUpStat = !isSuperAdmin && !isCallCentreAdmin;

  const canAccess = useMemo(() => {
    if (!user) return false;
    if (!embedded) {
      return (
        (isSuperAdmin || isCallCentreAdmin) &&
        dispositionAcl.canView &&
        dispositionAcl.canRead
      );
    }
    if (isSuperAdmin) return true;
    if (isAgent) return true;
    const acl = user?.acl_settings?.module_operations
      ? parseModuleOperations(user.acl_settings.module_operations)
      : {};
    return canUseDispositionFollowUpsFeature(acl, { isSuperAdmin: false });
  }, [user, isSuperAdmin, isCallCentreAdmin, isAgent, embedded, dispositionAcl]);

  const fetchData = useCallback((status: string, fromDate: string, toDate: string) => {
    let mounted = true;
    setLoading(true);
    setError('');

    const params: any = {};
    if (status && status !== 'overdue') params.status = status;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    getApiCall(API_DISPOSITION_FOLLOW_UPS_LIST, params)
      .then((resp: any) => {
        if (!mounted) return;
        let list = Array.isArray(resp?.follow_ups) ? resp.follow_ups : [];
        if (status === 'overdue') {
          list = list.filter((row: any) => row.effective_status === 'overdue');
        }
        setAllRows(list);
        if (keepSavedPageRef.current) keepSavedPageRef.current = false;
        else setPage(1);
      })
      .catch((err: any) => {
        if (!mounted) return;
        setError(typeof err === 'string' ? err : 'Failed to load follow-ups');
        setAllRows([]);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!canAccess || (embedded && !active)) {
      setLoading(false);
      return undefined;
    }
    const cleanup = fetchData(appliedStatus, appliedFromDate, appliedToDate);
    return cleanup;
  }, [canAccess, embedded, active, fetchData, appliedStatus, appliedFromDate, appliedToDate]);

  const filteredRows = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row: any) => {
      const haystack = [
        row.agent_name,
        row.agent_id,
        row.did_number,
        row.disposition_form_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allRows, appliedSearch]);

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    list.sort((a: any, b: any) => {
      let av;
      let bv;
      if (sortField === 'follow_up') {
        av = getFollowUpTimestamp(a);
        bv = getFollowUpTimestamp(b);
      } else if (sortField === 'created') {
        av = a.created_at ? new Date(a.created_at).getTime() : 0;
        bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      } else {
        return 0;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [filteredRows, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, sortedRows.length);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const stats = useMemo(() => {
    const pending = filteredRows.filter(
      (r: any) =>
        (r.effective_status || r.status) === 'pending' ||
        r.effective_status === 'overdue',
    ).length;
    const completed = filteredRows.filter((r: any) => r.status === 'completed').length;

    const upcoming = showNextFollowUpStat
      ? filteredRows
          .filter((r: any) => r.status === 'pending')
          .map((r: any) => ({ row: r, ts: getFollowUpTimestamp(r) }))
          .filter((x: any) => x.ts >= Date.now())
          .sort((a: any, b: any) => a.ts - b.ts)[0]
      : null;

    return {
      total: filteredRows.length,
      pending,
      completed,
      nextFollowUp: upcoming ? fmtFollowUpDateTime(upcoming.row) : '-',
    };
  }, [filteredRows, showNextFollowUpStat]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleFromDateChange = (value: string) => {
    const to = draftToDate || DEFAULT_FOLLOW_UP_DATE_RANGE.to;
    if (value && to && value > to) {
      toast.error('From date cannot be after to date');
      return;
    }
    if (value && to && !isFollowUpRangeWithinLimit(value, to)) {
      toast.error('Date range cannot exceed 3 months');
      return;
    }
    setDraftFromDate(value);
  };

  const handleToDateChange = (value: string) => {
    const from = draftFromDate || DEFAULT_FOLLOW_UP_DATE_RANGE.from;
    if (from && value && from > value) {
      toast.error('To date cannot be before from date');
      return;
    }
    if (from && value && !isFollowUpRangeWithinLimit(from, value)) {
      toast.error('Date range cannot exceed 3 months');
      return;
    }
    setDraftToDate(value);
  };

  const applyFilters = () => {
    const from = draftFromDate;
    const to = draftToDate;
    if (from && to && from > to) {
      toast.error('From date cannot be after to date');
      return;
    }
    if (from && to && !isFollowUpRangeWithinLimit(from, to)) {
      toast.error('Date range cannot exceed 3 months');
      return;
    }
    setAppliedStatus(draftStatus);
    setAppliedFromDate(from);
    setAppliedToDate(to);
    setAppliedSearch(draftSearch);
  };

  const resetFilters = () => {
    const { from, to } = getDefaultFollowUpDateRange();
    setDraftStatus('');
    setDraftFromDate(from);
    setDraftToDate(to);
    setDraftSearch('');
    setAppliedStatus('');
    setAppliedFromDate(from);
    setAppliedToDate(to);
    setAppliedSearch('');
    setPage(1);
  };

  const refresh = () => fetchData(appliedStatus, appliedFromDate, appliedToDate);

  const updateStatus = async (id: any, status: string) => {
    setUpdatingId(id);
    try {
      await patchApiCall(API_DISPOSITION_FOLLOW_UP_STATUS.replace(':id', id), {
        status,
      });
      toast.success(
        status === 'completed' ? 'Follow-up marked completed' : 'Follow-up cancelled',
      );
      refresh();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Unable to update follow-up');
    } finally {
      setUpdatingId(null);
    }
  };

  const detailFields = useMemo(() => {
    const fields = detailsRow?.disposition_details?.fields;
    if (!fields || typeof fields !== 'object') return [];
    return Object.entries(fields).map(([name, value]) => ({ name, value }));
  }, [detailsRow]);

  if (!canAccess) {
    return embedded ? null : <AclDenied operation="read" />;
  }

  const panel = (
    <div className={`card dfu-page dfu-card${embedded ? ' dfu-embedded' : ''}`}>
      <div className="dfu-header">
        <div>
          <h1 className="dfu-title">
            Disposition Follow-Ups
            <span className="dfu-title-badge">{stats.total}</span>
          </h1>
          <p className="dfu-subtitle">
            Scheduled callbacks from disposition submissions
          </p>
        </div>
        <div className="dfu-header-actions">
          {embedded && onClose ? (
            <button
              type="button"
              className="dfu-close-btn"
              onClick={onClose}
              aria-label="Close follow-ups"
            >
              <X aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
            </button>
          ) : null}
          <button
            type="button"
            className="dfu-refresh-btn"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshCw aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <DateRangeFilter
        className="px-6 pb-2 pt-3"
        value={{ from: draftFromDate, to: draftToDate }}
        onChange={({ from, to }) => {
          if (from !== draftFromDate) handleFromDateChange(from);
          else handleToDateChange(to);
        }}
        onApply={applyFilters}
        applyLabel="Apply Filters"
        onReset={resetFilters}
        inputClassName="dfu-filter-control"
        fromLabel="From date"
        toLabel="To date"
      >
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Status</label>
          <select
            className="dfu-filter-control"
            style={{ height: '40px', width: '140px' }}
            value={draftStatus}
            onChange={(e) => setDraftStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Search</label>
          <div className="dfu-search-wrap">
            <Search aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
            <input
              type="text"
              className="dfu-filter-control"
              style={{ height: '40px' }}
              placeholder="Search agent, contact or form..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilters();
              }}
            />
          </div>
        </div>
      </DateRangeFilter>

      <div className="dfu-stats">
        <div className="dfu-stat-card">
          <div>
            <div className="dfu-stat-label">Total Follow-Ups</div>
            <div className="dfu-stat-value">{stats.total}</div>
          </div>
          <div className="dfu-stat-icon blue">
            <Calendar aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
          </div>
        </div>
        <div className="dfu-stat-card">
          <div>
            <div className="dfu-stat-label">Pending</div>
            <div className="dfu-stat-value">{stats.pending}</div>
          </div>
          <div className="dfu-stat-icon orange">
            <Clock aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
          </div>
        </div>
        <div className="dfu-stat-card">
          <div>
            <div className="dfu-stat-label">Completed</div>
            <div className="dfu-stat-value">{stats.completed}</div>
          </div>
          <div className="dfu-stat-icon green">
            <Check aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
          </div>
        </div>
        {showNextFollowUpStat ? (
          <div className="dfu-stat-card">
            <div>
              <div className="dfu-stat-label">Next Follow-Up</div>
              <div className="dfu-stat-value dfu-stat-value-sm">{stats.nextFollowUp}</div>
            </div>
            <div className="dfu-stat-icon purple">
              <CalendarCheck aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
            </div>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="dfu-loading">
          <Spinner animation="border" size="sm" /> Loading follow-ups...
        </div>
      ) : error ? (
        <div className="dfu-loading text-danger">{error}</div>
      ) : sortedRows.length === 0 ? (
        <div className="dfu-empty">No follow-ups found for the selected filters.</div>
      ) : (
        <div className="dfu-table-wrap">
          <table className="dfu-table">
            <thead>
              <tr>
                {!isAgent ? <th>Agent</th> : null}
                <th>Contact</th>
                <th>Disposition Form</th>
                <SortHeader
                  label="Follow-Up"
                  field="follow_up"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th>Status</th>
                <SortHeader
                  label="Created"
                  field="created"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th>Details</th>
                {!isAgent ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row: any) => {
                const effectiveStatus = row.effective_status || row.status;
                const isPending = row.status === 'pending';
                return (
                  <tr key={row.id}>
                    {!isAgent ? (
                      <td>
                        <div className="dfu-agent-cell">
                          <div className="dfu-avatar">{getInitials(row.agent_name)}</div>
                          <div>
                            <div className="dfu-agent-name">{row.agent_name || '-'}</div>
                            <div className="dfu-agent-id">Agent ID: {row.agent_id}</div>
                          </div>
                        </div>
                      </td>
                    ) : null}
                    <td>
                      <span className="dfu-contact">
                        <Phone aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                        {row.did_number || '-'}
                      </span>
                    </td>
                    <td>{row.disposition_form_name || '-'}</td>
                    <td>
                      <span className="dfu-datetime">
                        <Calendar aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                        {fmtFollowUpDateTime(row)}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={effectiveStatus} />
                    </td>
                    <td>
                      <span className="dfu-datetime">
                        <Clock aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                        {fmtCreated(row.created_at)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="dfu-details-btn"
                        onClick={() => setDetailsRow(row)}
                        disabled={!row.disposition_form_data_id}
                      >
                        <FileText className="me-1" aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                        View
                      </button>
                    </td>
                    {!isAgent ? (
                      <td>
                        {isPending ? (
                          <div className="dfu-actions">
                            <button
                              type="button"
                              className="dfu-action-btn complete"
                              disabled={updatingId === row.id}
                              onClick={() => updateStatus(row.id, 'completed')}
                            >
                              <Check aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                              Complete
                            </button>
                            <button
                              type="button"
                              className="dfu-action-btn cancel"
                              disabled={updatingId === row.id}
                              onClick={() => updateStatus(row.id, 'cancelled')}
                            >
                              <X aria-hidden="true"
        style={{ width: 14, height: 14 }}
      />
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && sortedRows.length > 0 ? (
        <div className="dfu-footer">
          <div>
            Showing {pageStart} to {pageEnd} of {sortedRows.length} results
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <label className="mb-0 d-flex align-items-center">
              Rows per page:
              <select
                className="dfu-rows-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {LIST_PAGE_SIZE_OPTIONS.map((n: number) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div className="dfu-pagination">
              <button
                type="button"
                className="dfu-page-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (n) =>
                    n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1,
                )
                .map((n, idx, arr) => {
                  const prev = arr[idx - 1];
                  const showEllipsis = prev && n - prev > 1;
                  return (
                    <React.Fragment key={n}>
                      {showEllipsis ? (
                        <span className="px-1 text-muted">…</span>
                      ) : null}
                      <button
                        type="button"
                        className={`dfu-page-btn${currentPage === n ? ' active' : ''}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    </React.Fragment>
                  );
                })}
              <button
                type="button"
                className="dfu-page-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  const detailsModal = (
    <Modal
      show={Boolean(detailsRow)}
      onHide={() => setDetailsRow(null)}
      size="lg"
      centered
      className="dfu-details-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Disposition Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detailsRow ? (
          <div className="dfu-details-body">
            <div className="dfu-details-meta">
              <div>
                <span className="dfu-details-label">Form</span>
                <span>{detailsRow.disposition_form_name || '-'}</span>
              </div>
              <div>
                <span className="dfu-details-label">Contact</span>
                <span>{detailsRow.did_number || '-'}</span>
              </div>
              <div>
                <span className="dfu-details-label">Follow-Up</span>
                <span>{fmtFollowUpDateTime(detailsRow)}</span>
              </div>
              <div>
                <span className="dfu-details-label">Submitted</span>
                <span>
                  {fmtCreated(
                    detailsRow.disposition_details?.created_at ||
                      detailsRow.created_at,
                  )}
                </span>
              </div>
              <div>
                <span className="dfu-details-label">Source</span>
                <span>{sourceLabel(detailsRow.disposition_details?.disposition_source)}</span>
              </div>
              <div>
                <span className="dfu-details-label">Campaign</span>
                <span>{detailsRow.disposition_details?.campaign_name || '-'}</span>
              </div>
              {detailsRow.disposition_details?.call_sid ? (
                <div>
                  <span className="dfu-details-label">Call SID</span>
                  <span>{detailsRow.disposition_details.call_sid}</span>
                </div>
              ) : null}
              {detailsRow.trace_id ? (
                <div>
                  <span className="dfu-details-label">Trace ID</span>
                  <span>{detailsRow.trace_id}</span>
                </div>
              ) : null}
            </div>
            {detailFields.length ? (
              <div className="dfu-details-fields">
                <h6 className="dfu-details-fields-title">Submission fields</h6>
                <table className="dfu-details-table">
                  <tbody>
                    {detailFields.map(({ name, value }: any) => (
                      <tr key={name}>
                        <th>{name}</th>
                        <td>{formatDispositionFieldValue(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dfu-details-empty text-muted mb-0">
                No disposition field data is linked to this follow-up yet.
              </p>
            )}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setDetailsRow(null)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const accessDenied =
    !loading && !!error && /acl|permission|not enabled|forbidden/i.test(error);
  if (accessDenied) {
    const notice = (
      <div className="my-12 text-center text-base text-danger">{error}</div>
    );
    return embedded ? notice : <FillPage>{notice}</FillPage>;
  }

  if (embedded) {
    return (
      <>
        <div className="dfu-embedded-shell">{panel}</div>
        {detailsModal}
      </>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Disposition Follow-Ups"
        description="Follow-up tasks agents flagged during disposition — sorted by due time."
      />
      <Row className="mb-4">
        <Col lg="12">{panel}</Col>
      </Row>
      {detailsModal}
    </FillPage>
  );
};

export default DispositionFollowUpsList;
